import React, { useState, useEffect } from 'react';
import { getMyOrg, purchaseTopUp, getTopUpHistory } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const PLAN_LABELS = { essential:'Essential', standard:'Standard', enterprise:'Enterprise' };
const STATUS_COLORS = { active:'var(--success)', suspended:'var(--danger)', cancelled:'var(--muted)' };

const TOPUP_RATES = { essential: 8, standard: 6 };

export default function SubscriptionPage() {
  const [org,      setOrg]      = useState(null);
  const [limits,    setLimits]    = useState(null);
  const [topUps,    setTopUps]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [buying,       setBuying]       = useState(false);
  const [qty,          setQty]          = useState(10);
  const [contactModal, setContactModal] = useState(false);
  const { adminEmail } = useAuth();

  const load = async () => {
    try {
      const [orgRes, tuRes] = await Promise.all([getMyOrg(), getTopUpHistory()]);
      setOrg(orgRes.data.organisation);
      setLimits(orgRes.data.limits);
      setTopUps(tuRes.data);
    } catch { toast.error('Failed to load subscription'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleTopUp = async () => {
    setBuying(true);
    try {
      const res = await purchaseTopUp({ quantity: qty });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      setContactModal(true);
    } finally { setBuying(false); }
  };


  if (loading) return <div style={{padding:'2rem',color:'var(--muted)'}}>Loading subscription…</div>;
  if (!org)    return <div style={{padding:'2rem',color:'var(--muted)'}}>No subscription data available.</div>;

  const quotaLimit    = limits?.quotations || null;
  const quotaUsed     = org.quota_used || 0;
  const quotaPct      = quotaLimit ? Math.min(100, Math.round((quotaUsed / quotaLimit) * 100)) : 0;
  const resetDate     = org.quota_reset_at ? new Date(org.quota_reset_at).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'}) : '—';
  const ratePerTen    = TOPUP_RATES[org.plan] || 8;
  const topupCost     = `$${((qty / 10) * ratePerTen).toFixed(2)}`;

  return (
    <div>
      <div style={{marginBottom:24}}>
        <h1 className="page-title">My Subscription</h1>
        <p className="page-sub">Manage your plan, quotation usage, and top-ups.</p>
      </div>

      {/* Plan overview */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:14,marginBottom:20}}>
        {[
          { label:'Plan',         value: PLAN_LABELS[org.plan] || org.plan },
          { label:'Status',       value: org.status, color: STATUS_COLORS[org.status] },
          { label:'Billing',      value: org.billing_cycle === 'annual' ? 'Annual' : 'Monthly' },
          { label:'Quota resets', value: resetDate },
          { label:'Credit balance', value: `${org.credit_balance || 0} quotations` },
        ].map(s => (
          <div key={s.label} className="card" style={{padding:'14px 18px'}}>
            <div style={{fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'.06em',color:'var(--muted)',marginBottom:6}}>{s.label}</div>
            <div style={{fontSize:18,fontWeight:700,color: s.color || 'var(--text)'}}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Quota usage */}
      {quotaLimit !== null && (
        <div className="card" style={{marginBottom:20}}>
          <div className="section-header">
            <div className="section-header-dot"/>
            <span style={{fontWeight:600,fontSize:13.5}}>
              <i className="ti ti-chart-bar" style={{marginRight:6,color:'var(--precision)'}} aria-hidden="true"/>
              Quotation usage this month
            </span>
          </div>
          <div className="card-body">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:10}}>
              <span style={{fontSize:13,color:'var(--text2)'}}>Used <strong style={{color:'var(--text)'}}>{quotaUsed}</strong> of <strong style={{color:'var(--text)'}}>{quotaLimit}</strong> included quotations</span>
              <span style={{fontSize:13,fontWeight:600,color: quotaPct >= 90 ? 'var(--danger)' : quotaPct >= 70 ? 'var(--warning)' : 'var(--success)'}}>{quotaPct}%</span>
            </div>
            <div style={{background:'var(--surface-2)',borderRadius:6,height:10,overflow:'hidden',border:'1px solid var(--border)'}}>
              <div style={{
                height:'100%',borderRadius:6,transition:'width .4s',
                background: quotaPct >= 90 ? 'var(--danger)' : quotaPct >= 70 ? 'var(--warning)' : 'var(--precision)',
                width: `${quotaPct}%`,
              }}/>
            </div>
            {org.credit_balance > 0 && (
              <div style={{marginTop:10,fontSize:13,color:'var(--success)'}}>
                <i className="ti ti-bolt" style={{marginRight:4}} aria-hidden="true"/>
                {org.credit_balance} top-up quotations available
              </div>
            )}
          </div>
        </div>
      )}

      {/* Countries */}
      <div className="card" style={{marginBottom:20}}>
        <div className="section-header">
          <div className="section-header-dot"/>
          <span style={{fontWeight:600,fontSize:13.5}}>
            <i className="ti ti-world" style={{marginRight:6,color:'var(--precision)'}} aria-hidden="true"/>
            Active countries
          </span>
        </div>
        <div className="card-body">
          <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
            {(org.allowed_countries || []).map(c => (
              <span key={c} style={{background:'var(--cloud)',color:'var(--precision-h)',border:'1px solid var(--sky)',padding:'4px 12px',borderRadius:20,fontSize:12.5,fontWeight:500}}>{c}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Top-up purchase */}
      {org.plan !== 'enterprise' && (
        <div className="card" style={{marginBottom:20}}>
          <div className="section-header">
            <div className="section-header-dot"/>
            <span style={{fontWeight:600,fontSize:13.5}}>
              <i className="ti ti-bolt" style={{marginRight:6,color:'var(--precision)'}} aria-hidden="true"/>
              Purchase top-up quotations
            </span>
          </div>
          <div className="card-body">
            <p style={{fontSize:13,color:'var(--text2)',marginBottom:16,lineHeight:1.6}}>
              Top-ups are charged immediately and credited to your account at once. Rate: <strong>${ratePerTen} per 10 quotations</strong>.
            </p>
            <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">Quantity</label>
                <select className="form-control" value={qty} onChange={e => setQty(parseInt(e.target.value))} style={{width:160}}>
                  <option value={10}>10 quotations — ${ratePerTen}</option>
                  <option value={20}>20 quotations — ${ratePerTen*2}</option>
                  <option value={50}>50 quotations — ${ratePerTen*5}</option>
                </select>
              </div>
              <div style={{paddingTop:18}}>
                <button className="btn btn-primary" onClick={handleTopUp} disabled={buying}>
                  {buying ? <><div className="spinner"/>Processing…</> : <><i className="ti ti-bolt" aria-hidden="true"/>Buy {qty} for {topupCost}</>}
                </button>
              </div>
              <div style={{paddingTop:18,fontSize:12,color:'var(--muted)'}}>
                Charged immediately to your card on file
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top-up history */}
      <div className="card">
        <div className="section-header">
          <div className="section-header-dot"/>
          <span style={{fontWeight:600,fontSize:13.5}}>Top-up history</span>
        </div>
        <div style={{overflowX:'auto'}}>
          {topUps.length === 0 ? (
            <div style={{padding:'24px',textAlign:'center',color:'var(--muted)',fontSize:13}}>No top-ups purchased yet</div>
          ) : (
            <table className="table">
              <thead>
                <tr><th>Date</th><th>Quotations</th><th>Amount</th><th>Status</th></tr>
              </thead>
              <tbody>
                {topUps.map(t => (
                  <tr key={t.id}>
                    <td>{new Date(t.createdAt).toLocaleDateString('en-GB')}</td>
                    <td>{t.quantity}</td>
                    <td>${(t.amount_cents/100).toFixed(2)}</td>
                    <td>
                      <span className={`badge ${t.status === 'succeeded' ? 'badge-green' : t.status === 'failed' ? 'badge-red' : 'badge-yellow'}`}>
                        {t.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Contact administrator modal */}
      {contactModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20,backdropFilter:'blur(2px)'}}>
          <div style={{background:'var(--surface)',borderRadius:16,width:'100%',maxWidth:420,boxShadow:'0 24px 64px rgba(0,0,0,.18)',border:'1px solid var(--border)',overflow:'hidden'}}>
            <div style={{background:'#0C1220',padding:'20px 24px',display:'flex',alignItems:'center',gap:12}}>
              <div style={{width:40,height:40,borderRadius:10,background:'rgba(14,165,233,.2)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <i className="ti ti-mail" style={{fontSize:20,color:'#38BDF8'}} aria-hidden="true"/>
              </div>
              <div>
                <div style={{color:'#fff',fontWeight:700,fontSize:15}}>Contact your administrator</div>
                <div style={{color:'rgba(255,255,255,.45)',fontSize:12,marginTop:2}}>Payment not yet configured</div>
              </div>
            </div>
            <div style={{padding:'20px 24px'}}>
              <p style={{fontSize:13.5,color:'var(--text2)',lineHeight:1.7,marginBottom:20}}>
                Online payment is not configured yet for this platform. To purchase a top-up, please contact your administrator and they will add quotation credits to your account.
              </p>
              <a
                href={`mailto:${adminEmail}?subject=Top-up request&body=Hi,%0A%0AI would like to purchase a top-up of ${qty} quotations for my Quoteology account. Please assist.%0A%0AThank you.`}
                style={{
                  display:'flex',alignItems:'center',justifyContent:'center',gap:8,
                  width:'100%',padding:'11px',
                  background:'var(--precision)',color:'#fff',
                  borderRadius:8,fontSize:14,fontWeight:600,
                  textDecoration:'none',marginBottom:10,
                  boxSizing:'border-box',
                }}>
                <i className="ti ti-mail" aria-hidden="true"/> Email {adminEmail}
              </a>
              <button
                onClick={() => setContactModal(false)}
                style={{width:'100%',padding:'10px',background:'transparent',border:'1px solid var(--border)',borderRadius:8,fontSize:13,color:'var(--text2)',cursor:'pointer',fontFamily:'inherit'}}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}